import React from 'react';
import { TableCellProps } from '../../../types/search';
import ImagesModal from './ImagesModal';
import AttachmentsModal from './AttachmentsModal';
import DownloadDocsBtnIndividual from './DownloadDocsBtnIndividual';
import PayInvoiceBtn from './PayInvoiceBtn';
import { formatDate, frequencyParts, moneyFormat, getStatusClass, addPaidDate } from '../utils/tableCellUtils';
import { useSearchConfig } from '../../Search/hooks/useSearchConfig';

const ServicesMobileCell: React.FC<TableCellProps> = ({ row, listingType }) => {
  const statusClass = getStatusClass(row['status']);
  const { frequency, day } = frequencyParts(row['frequency']);

  return (
    <div className={`portal-cell portal-cell--mobile portal-cell--mobile-${listingType}`}>
      <div className="portal-cell__wrapper">
        <p>
          <strong>{row['wasteType']}</strong>
        </p>
        {row['bin'] && <p>{row['bin']}</p>}
      </div>
      <div className="portal-cell__wrapper">
        <p>
          <strong>{frequency}</strong>
        </p>
        {day && <p>Every {day}</p>}
      </div>
      <div className="portal-cell__wrapper">
        <p>
          <strong>{row['site']}</strong>
        </p>
        {row['postcode'] && <p>{row['postcode']}</p>}
      </div>
      <div className="portal-cell__wrapper">
        <span className={`portal-cell__pill portal-cell__pill--${statusClass}`}>{row['status']}</span>
      </div>
    </div>
  );
};

const InvoicesMobileCell: React.FC<TableCellProps> = ({ row, listingType }) => {
  const { labels, invoicePaymentUrl } = useSearchConfig();
  const statusClass = getStatusClass(row['status'], listingType);
  const showPayButton = row['outstandingBalance'] > 0;
  const downloadDocsBtnPlacement = showPayButton ? 'bottom end' : 'bottom start';
  const dueDate = formatDate(row['dateDue']);
  const dateIssued = formatDate(row['dateIssued']);
  const totalAmount = moneyFormat(row['total']);
  const outstandingBalance = moneyFormat(row['outstandingBalance']);
  const status = listingType === 'invoices' ? addPaidDate(row['status'], row.paidDate) : row['status'];

  return (
    <div className={`portal-cell portal-cell--mobile portal-cell--mobile-${listingType}`}>
      <div className="portal-cell__row">
        <div className="portal-cell__wrapper">
          <p className="portal-cell__bold-underline-text">{row['referenceId']}</p>
        </div>
        <div className="portal-cell__wrapper">
          <p className="portal-cell__mobile-label">Due</p>
          <p>{dueDate}</p>
        </div>
        <div className="portal-cell__wrapper">
          <p className="portal-cell__mobile-label">Issued</p>
          <p>{dateIssued}</p>
        </div>
        <div className="portal-cell__wrapper">
          <p className="portal-cell__mobile-label">Total</p>
          <p>{totalAmount}</p>
        </div>
        <div className="portal-cell__wrapper">
          <p className="portal-cell__mobile-label">Left to pay</p>
          <p>{outstandingBalance}</p>
        </div>
        <div className="portal-cell__wrapper">
          <span className={`portal-cell__pill portal-cell__pill--${statusClass}`}>{status}</span>
        </div>
      </div>
      <div className="portal-cell__wrapper">
        <PayInvoiceBtn
          invoiceId={row['id']}
          invoiceNumber={row['referenceId']}
          dateDue={row['dateDue']}
          amount={row['outstandingBalance']}
          payButtonLabel={labels.payButtonLabel}
          invoicePaymentUrl={invoicePaymentUrl}
          additionalCtaClass="portal-cell__pay-btn"
        />
        <DownloadDocsBtnIndividual rowId={row['id']} placement={downloadDocsBtnPlacement} />
      </div>
    </div>
  );
};

const JobsMobileCell: React.FC<TableCellProps> = ({ row, listingType }) => {
  const statusClass = getStatusClass(row['status']);
  const parentClass = 'status-pill';

  return (
    <div className={`portal-cell portal-cell--mobile portal-cell--mobile-${listingType}`}>
      <div className="portal-cell__row">
        <div className="portal-cell__wrapper">
          <p>
            <strong>{row.serviceName}</strong>
          </p>
          {row.containerType ? <p>{row.containerType}</p> : null}
        </div>
        <div className="portal-cell__wrapper">
          <p>{row.action}</p>
        </div>
        <div className="portal-cell__wrapper">
          <p>
            <strong>{row.siteName}</strong>
          </p>
          {row.sitePostcode ? <p>{row.sitePostcode}</p> : null}
        </div>
        <div className="portal-cell__wrapper portal-cell__wrapper--status">
          <span className={`${parentClass} ${parentClass}--${statusClass}`}>{row['status']}</span>
          <div className="portal-cell--attachments">
            <div className="portal-cell__trigger-btns-wrap">
              <ImagesModal row={row} listingType={listingType} />
              <AttachmentsModal row={row} listingType={listingType} />
            </div>
          </div>
        </div>
      </div>
      <div className="portal-cell__wrapper portal-cell__wrapper--attachments">
        <div className="portal-cell--attachments">
          <div className="portal-cell__trigger-btns-wrap">
            <ImagesModal row={row} listingType={listingType} />
            <AttachmentsModal row={row} listingType={listingType} />
          </div>
        </div>
      </div>
    </div>
  );
};

const PortalCellMobile: React.FC<TableCellProps> = ({ row, listingType }) => {
  if (listingType === 'services') {
    return <ServicesMobileCell row={row} listingType={listingType} />;
  } else if (listingType === 'invoices') {
    return <InvoicesMobileCell row={row} listingType={listingType} />;
  } else if (listingType === 'jobs') {
    return <JobsMobileCell row={row} listingType={listingType} />;
  }
};

export default PortalCellMobile;
